import React, { useEffect, useRef } from 'react';
import { Card, Col, Container, Row } from 'react-bootstrap';
import Chart from 'chart.js/auto';
import { getDaysBetweenDates, parseAsUTC } from '../../../../utils/time';
import { PatientVisit } from '../../../../amplify/appsync/search-patients';

interface TrendsPanelProps {
    patientVisit: PatientVisit;
    scanData: any;
    expandedSection: string;
}

interface ChartData {
    x: number;
    y: number;
    studyDate?: string;
    display_name?: string;
    unit?: string;
    measurement?: string;
    rhythm?: string;
    impression?: string;
    count?: number;
    color?: string;
}

interface ChartConfig {
    yAxisLabel: string;
    xAxisConfig: any;
    showLegend?: boolean;
    tooltipCallback?: (item: any) => string[];
}

export const PhysiologyDashboardTrendsPanel: React.FC<TrendsPanelProps> = ({ patientVisit, scanData, expandedSection }) => {
    // const chartRefs = useRef<{ [key: string]: HTMLCanvasElement | null }>({});

    const [chartInstances, setChartInstances] = React.useState<any>({});
    const [cardiacData, setCardiacData] = React.useState<any>({});
    const [pulmonaryData, setPulmonaryData] = React.useState<any>({});
    const [renalData, setRenalData] = React.useState<any>({});
    const [liverData, setLiverData] = React.useState<any>({});

    const mountedRef = React.useRef(false);

    // Helper function to get point color based on value and ranges
    const getPointColor = (type: string, value: number, componentAbnormalFlag: any = null) => {
        const pointColors = {
            normal: 'rgba(54, 162, 235, 0.8)',   // Blue
            abnormal: 'rgba(234, 179, 8, 0.8)',  // Yellow-600
            critical: 'rgba(220, 38, 38, 0.8)',  // Red-600
            default: 'rgba(107, 114, 128, 0.8)'  // Gray-500
        };

        const criticalValueRanges: any = {
            hr: { normal: [60, 100], abnormal: [[41, 59], [101, 130]], critical: [[-Infinity, 40], [131, Infinity]] },
            nbps: { normal: [100, 129], abnormal: [[90, 99], [130, 179]], critical: [[-Infinity, 89], [180, Infinity]] },
            nbpd: { normal: [60, 79], abnormal: [[50, 59], [80, 119]], critical: [[-Infinity, 49], [120, Infinity]] },
            map: { normal: [70, 105], abnormal: [[60, 69], [106, 120]], critical: [[-Infinity, 59], [121, Infinity]] },
            spo2: { normal: [95, Infinity], abnormal: [[90, 94]], critical: [[-Infinity, 89]] },
            rr: { normal: [12, 20], abnormal: [[10, 11], [21, 29]], critical: [[-Infinity, 9], [30, Infinity]] },
            LVEF: { normal: [55, Infinity], abnormal: [[41, 54]], critical: [[-Infinity, 40]] },
            RVSP: { normal: [-Infinity, 35], abnormal: [[36, 50]], critical: [[51, Infinity]] },
            QTc: {
                male: { normal: [-Infinity, 430], abnormal: [[431, 470]], critical: [[471, Infinity]] },
                female: { normal: [-Infinity, 450], abnormal: [[451, 470]], critical: [[471, Infinity]] },
                default: { normal: [-Infinity, 430], abnormal: [[431, 470]], critical: [[471, Infinity]] }
            }
        };

        const checkRange = (val: number, range: [number, number]): boolean => {
            return val >= range[0] && val <= range[1];
        };

        if (value === null || value === undefined || isNaN(value)) return pointColors.default;

        // Handle labs first using componentAbnormalFlag if available
        if (componentAbnormalFlag) {
            const flagLower = (componentAbnormalFlag || '').toLowerCase();
            if (flagLower.includes('critical')) return pointColors.critical;
            if (flagLower.includes('abnormal')) return pointColors.abnormal;
            // Consider 'Normal', '', null, undefined as normal
            if (flagLower === 'normal' || flagLower === '' || componentAbnormalFlag === null) return pointColors.normal;
            // Otherwise return default (e.g. for 'High', 'Low' without 'Abnormal'/'Critical')
            return pointColors.default;
        }

        // Handle QTc with gender-specific ranges
        if (type === 'QTc') {
            // Try to determine gender from patient demographics
            const gender = scanData?.demographics?.gender?.toLowerCase() || 'default';
            const genderKey = (gender.includes('female') || gender.includes('f')) ? 'female' :
                (gender.includes('male') || gender.includes('m')) ? 'male' : 'default';

            const ranges = criticalValueRanges.QTc[genderKey];
            if (ranges) {
                if (checkRange(value, ranges.critical)) return pointColors.critical;
                if (checkRange(value, ranges.abnormal)) return pointColors.abnormal;
                if (value >= ranges.normal[0] && value <= ranges.normal[1]) return pointColors.normal;
            }
            return pointColors.default;
        }

        // Handle VS and Echo using defined ranges if no flag provided
        const ranges = criticalValueRanges[type];
        if (ranges) {
            if (checkRange(value, ranges.critical)) return pointColors.critical;
            if (checkRange(value, ranges.abnormal)) return pointColors.abnormal;
            if (value >= ranges.normal[0] && value <= ranges.normal[1]) return pointColors.normal;
        }

        // Default if type not found or value doesn't fit ranges
        return pointColors.default;
    };

    // Function to create a chart
    const createChart = (
        canvasId: string,
        title: string,
        data: ChartData[],
        config: ChartConfig
    ) => {
        console.log(`[createChart] Called for ${canvasId}`); // DEBUG
        if (!mountedRef.current) return;
        const ctx = document.getElementById(canvasId);
        if (!ctx) {
            console.error(`[createChart] Canvas element not found: ${canvasId}`); // DEBUG
            return;
        }
        console.log(`[createChart] Found canvas element: ${canvasId}`, ctx); // DEBUG

        // Destroy existing chart if it exists
        if (chartInstances[canvasId]) {
            console.log(`[createChart] Destroying existing chart: ${canvasId}`); // DEBUG
            chartInstances[canvasId].destroy();
        }

        // @ts-ignore
        const newChart = new Chart(ctx, {
            type: 'scatter',
            data: {
                datasets: [{
                    label: title,
                    data: data,
                    pointBackgroundColor: data.map(d => d.color),
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    showLine: true,
                    borderColor: '#6b7280',
                    borderWidth: 1
                }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        type: 'linear',
                        position: 'bottom',
                        title: {
                            display: true,
                            text: 'Days Ago'
                        },
                        reverse: true,
                        offset: true,
                        ...config.xAxisConfig
                    },
                    y: {
                        title: {
                            display: true,
                            text: config.yAxisLabel
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: config.showLegend !== false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context: any) => {
                                const item = context.raw;
                                if (config.tooltipCallback) {
                                    return config.tooltipCallback(item);
                                }
                                return [`${item.y} ${item.unit || ''}`];
                            }
                        }
                    }
                }
            }
        });

        setChartInstances((prev: any) => ({
            ...prev,
            [canvasId]: newChart
        }));
    };

    // Initialize charts when component mounts or data changes
    useEffect(() => {
        if (!scanData) {
            return;
        }

        // const { datasets, maxDaysAgo } = prepareCardiacData();
        // const commonXAxisConfig = {
        //     reverse: true,
        //     min: 0,
        //     max: Math.max(...Object.values(datasets).flatMap((d: any) => d.map((p: any) => p.x))),
        // };

        // // Create charts for each dataset
        // if (datasets.hr.length > 0) {
        //     createChart('hr-chart', 'Heart Rate', datasets.hr, {
        //         yAxisLabel: 'HR (bpm)',
        //         xAxisConfig: commonXAxisConfig,
        //         showLegend: false
        //     });
        // }

        // if (datasets.bp.length > 0) {
        //     // Group BP data by measurement type
        //     createChart('bp-chart', 'Blood Pressure', datasets.bp, {
        //         yAxisLabel: 'Pressure (mmHg)',
        //         xAxisConfig: commonXAxisConfig,
        //         showLegend: false,
        //         tooltipCallback: (item) => {
        //             return [
        //                 `Date: ${item.studyDate}`,
        //                 `${item.measurement}: ${item.y} mmHg`
        //             ];
        //         }
        //     });
        // }

        // if (datasets.qtc.length > 0) {
        //     createChart('qtc-chart', 'QTc Interval', datasets.qtc, {
        //         yAxisLabel: 'QTc (ms)',
        //         xAxisConfig: commonXAxisConfig,
        //         showLegend: false,
        //         tooltipCallback: (item) => {
        //             const value = item.y;
        //             const gender = scanData?.demographics?.gender?.toLowerCase() || 'unknown';
        //             let riskCategory = '';

        //             if (gender.includes('female') || gender.includes('f')) {
        //                 if (value <= 450) riskCategory = 'Normal';
        //                 else if (value <= 470) riskCategory = 'Abnormal';
        //                 else riskCategory = value >= 500 ? 'Critical (high risk for torsades)' : 'Critical';
        //             } else {
        //                 if (value <= 430) riskCategory = 'Normal';
        //                 else if (value <= 470) riskCategory = 'Abnormal';
        //                 else riskCategory = value >= 500 ? 'Critical (high risk for torsades)' : 'Critical';
        //             }

        //             return [
        //                 `Date: ${item.studyDate}`,
        //                 `QTc: ${value} ms (${riskCategory})`,
        //                 `Rhythm: ${item.rhythm}`
        //             ];
        //         }
        //     });
        // }
    }, [scanData]);

    // --- Data Preparation Functions ---

    // Prepare Cardiac data for charts
    const prepareCardiacData = () => {
        // Initialize datasets for LVEF, RVSP, HR, SBP, DBP, MAP, QTc
        const datasets: any = {
            lvef: [], rvsp: [], hr: [], bp: [], qtc: []
        };

        // Process LVEF from echo_results
        if (scanData.echo) {
            scanData.echo.forEach((studyData: any) => {
                if (!studyData.result_time) {
                    return;
                }

                const studyDate = parseAsUTC(studyData.result_time);

                // Process LVEF from echo
                if (studyData.lvef) {
                    Object.entries(studyData.lvef).forEach(([key, value]: any) => {
                        if (key === "LEFT VENTRICULAR EJECTION FRACTION EF(HM)_PHL") return;
                        if (value.value === null || isNaN(value.value)) return; // Skip null/NaN values
                        datasets.lvef.push({
                            x: getDaysBetweenDates(studyDate, patientVisit.departure_time ?? new Date()), y: value.value, studyDate: studyDate,
                            display_name: studyData.display_name || 'Echo Study', unit: value.unit,
                            measurement: key,
                            color: getPointColor('LVEF', value.value) // Add color
                        });
                    });
                }

                // Process RVSP from echo
                if (studyData.rvsp) {
                    Object.entries(studyData.rvsp).forEach(([key, value]: any) => {
                        if (value.value === null || isNaN(value.value)) return; // Skip null/NaN values
                        datasets.rvsp.push({
                            x: getDaysBetweenDates(studyDate, patientVisit.departure_time ?? new Date()), y: value.value, studyDate: studyDate,
                            display_name: studyData.display_name || 'Echo Study', unit: value.unit,
                            measurement: key,
                            color: getPointColor('RVSP', value.value) // Add color
                        });
                    });
                }
            });
        }

        // Process QTc from ecg_results
        if (scanData.ecg_12L) {
            scanData.ecg_12L.forEach((studyData: any) => {
                if (!studyData.result_time) {
                    return;
                }

                const studyDate = parseAsUTC(studyData.result_time);
                if (studyData.measurements && studyData.measurements["QTC INTERVAL"] !== null && !isNaN(studyData.measurements["QTC INTERVAL"])) {
                    datasets.qtc.push({
                        x: getDaysBetweenDates(studyDate, patientVisit.departure_time ?? new Date()), y: studyData.measurements["QTC INTERVAL"], studyDate: studyDate,
                        rhythm: studyData.rhythm || 'Unknown', impression: studyData.impression || '',
                        color: getPointColor('QTc', studyData.measurements["QTC INTERVAL"]) // Add color
                    });
                }
            });
        }

        // Process vital signs (HR, SBP, DBP, MAP) from vs_stats_by_csn
        if (scanData.vs_stats_by_csn) {
            Object.entries(scanData.vs_stats_by_csn).forEach(([csn, visitData]: any) => {
                const commonVsData = {
                    csn: csn,
                    startTime: visitData.start_time,
                    endTime: visitData.end_time,
                    dispo: visitData.dispo_class || 'Unknown'
                };

                // console.log("visitData", visitData);

                // HR median
                // Note the `!=` to capture both null and undefined
                if (visitData.measures?.hr?.median != null) {
                    const value = visitData.measures.hr.median;
                    datasets.hr.push({
                        ...commonVsData,
                        x: getDaysBetweenDates(visitData.start_time, patientVisit.departure_time ?? new Date()),
                        y: value,
                        count: visitData.measures.hr.count || 0,
                        color: getPointColor('hr', value)
                    });
                }

                // Process blood pressure measurements
                ['nbps', 'nbpd', 'map'].forEach(measure => {
                    const value = visitData.measures?.[measure]?.median;
                    if (value !== null && value !== undefined) {
                        datasets.bp.push({
                            ...commonVsData,
                            x: getDaysBetweenDates(visitData.start_time, patientVisit.departure_time ?? new Date()),
                            y: value,
                            count: visitData.measures[measure].count || 0,
                            color: getPointColor(measure, value),
                            measurement: measure // Add measurement type for tooltip
                        });
                    }
                });
            });
        }

        // Sort all datasets by days_ago (x-value) descending
        Object.keys(datasets).forEach(key => datasets[key].sort((a: any, b: any) => b.x - a.x));

        // Find max days_ago across all datasets for consistent x-axis scaling
        let maxDaysAgo = 0;
        Object.values(datasets).forEach((dataset: any) => {
            if (dataset.length > 0) {
                const datasetMaxDaysAgo = Math.max(...dataset.map((d: any) => d.x));
                maxDaysAgo = Math.max(maxDaysAgo, datasetMaxDaysAgo);
            }
        });

        return { datasets, maxDaysAgo };
    };

    // Prepare Pulmonary data for charts
    const preparePulmonaryData = () => {
        const datasets: any = { rr: [], spo2: [], pco2: [], hco3: [] };
        const labNames = {
            pco2: ['POC:PCO2 (V), ISTAT', 'PCO2 (v), ISTAT'], // Old naming vs new naming
            hco3: ['POC:HCO3 (V), ISTAT', 'HCO3 (v), ISTAT']
        };

        // Process RR, SpO2 from vs_stats_by_csn
        if (scanData.vs_stats_by_csn) {
            Object.entries(scanData.vs_stats_by_csn).forEach(([csn, visitData]: any) => {
                const commonVsData = {
                    csn: csn,
                    startTime: visitData.start_time,
                    endTime: visitData.end_time,
                    dispo: visitData.dispo_class || 'Unknown'
                };

                if (visitData.measures?.rr?.median != null) {
                    const value = visitData.measures.rr.median;
                    datasets.rr.push({
                        ...commonVsData,
                        x: getDaysBetweenDates(visitData.start_time, patientVisit.departure_time ?? new Date()),
                        y: value,
                        count: visitData.measures.rr.count || 0,
                        color: getPointColor('rr', value)
                    });
                }

                if (visitData.measures?.spo2?.median != null) {
                    const value = visitData.measures.spo2.median;
                    datasets.spo2.push({
                        ...commonVsData,
                        x: getDaysBetweenDates(visitData.start_time, patientVisit.departure_time ?? new Date()),
                        y: value,
                        count: visitData.measures.spo2.count || 0,
                        color: getPointColor('spo2', value)
                    });
                }
            });
        }

        // Process POC labs from labs
        if (scanData.labs) {
            Object.entries(labNames).forEach(([key, candidateLabNames]: any) => {
                for (let labName of candidateLabNames) {
                    // Check if labName exists and has a 'results' array
                    if (scanData.labs[labName] && scanData.labs[labName].results) {
                        // Iterate over the 'results' array
                        scanData.labs[labName].results.forEach((res: any) => {
                            // Convert string value to number, handle potential errors
                            const numericValue = parseFloat(res.component_value);
                            if (res.component_value !== null && !isNaN(numericValue)) {
                                datasets[key].push({
                                    x: getDaysBetweenDates(res.result_time, patientVisit.departure_time ?? new Date()),
                                    y: numericValue,
                                    resultTime: res.result_time,
                                    unit: scanData.labs[labName].component_units || '',
                                    abnormalFlag: res.component_abnormal,
                                    color: getPointColor(key.toUpperCase(), numericValue, res.component_abnormal)
                                });
                            }
                        });
                    }
                }
            });
        }

        Object.keys(datasets).forEach(key => datasets[key].sort((a: any, b: any) => b.x - a.x));
        let maxDaysAgo = 0;
        Object.values(datasets).forEach((dataset: any) => {
            if (dataset.length > 0) {
                const datasetMaxDaysAgo = Math.max(...dataset.map((d: any) => d.x));
                maxDaysAgo = Math.max(maxDaysAgo, datasetMaxDaysAgo);
            }
        });
        return { datasets, maxDaysAgo };
    };

    // Prepare Renal data for charts
    const prepareRenalData = () => {
        const datasets: any = { egfr: [] };
        const labNameSubstring = 'EGFR';

        // Check if labName exists and has a 'results' array
        if (scanData.labs) {
            for (let labName of Object.keys(scanData.labs)) {
                if (labName.toUpperCase().includes(labNameSubstring) && scanData.labs[labName] && scanData.labs[labName].results) {
                    // Iterate over the 'results' array
                    scanData.labs[labName].results.forEach((res: any) => {
                        // Convert string value to number, handle potential errors
                        const numericValue = parseFloat(res.component_value);
                        if (res.component_value !== null && !isNaN(numericValue)) {
                            datasets.egfr.push({
                                x: getDaysBetweenDates(res.result_time, patientVisit.departure_time ?? new Date()),
                                y: numericValue,
                                resultTime: res.result_time,
                                unit: scanData.labs[labName].component_units || '',
                                abnormalFlag: res.component_abnormal,
                                color: getPointColor('EGFR', numericValue, res.component_abnormal)
                            });
                        }
                    });
                }
            }
        }

        datasets.egfr.sort((a: any, b: any) => b.x - a.x);
        let maxDaysAgo = 0;
        if (datasets.egfr.length > 0) {
            maxDaysAgo = Math.max(...datasets.egfr.map((d: any) => d.x));
        }
        return { datasets, maxDaysAgo };
    };

    // Prepare Liver data for charts
    const prepareLiverData = () => {
        const datasets: any = { inr: [], albumin: [], bilirubin: [] };
        const labNames = {
            inr: ['INR'],
            albumin: ['ALBUMIN', 'Albumin, Ser/Plas'],
            bilirubin: ['BILIRUBIN, TOTAL', 'Total Bilirubin, Ser/Plas']
        };

        if (scanData.labs) {
            Object.entries(labNames).forEach(([key, candidateLabNames]: any) => {
                for (let labName of candidateLabNames) {
                    // Check if labName exists and has a 'results' array
                    if (scanData.labs[labName] && scanData.labs[labName].results) {
                        // Iterate over the 'results' array
                        scanData.labs[labName].results.forEach((res: any) => {
                            // Convert string value to number, handle potential errors
                            const numericValue = parseFloat(res.component_value);
                            if (res.component_value !== null && !isNaN(numericValue)) {
                                datasets[key].push({
                                    x: getDaysBetweenDates(res.result_time, patientVisit.departure_time ?? new Date()),
                                    y: numericValue,
                                    resultTime: res.result_time,
                                    unit: scanData.labs[labName].component_units || '',
                                    abnormalFlag: res.component_abnormal,
                                    color: getPointColor(key.toUpperCase(), numericValue, res.component_abnormal)
                                });
                            }
                        });
                    }
                }
            });
        }

        Object.keys(datasets).forEach(key => datasets[key].sort((a: any, b: any) => b.x - a.x));
        let maxDaysAgo = 0;
        Object.values(datasets).forEach((dataset: any) => {
            if (dataset.length > 0) {
                const datasetMaxDaysAgo = Math.max(...dataset.map((d: any) => d.x));
                maxDaysAgo = Math.max(maxDaysAgo, datasetMaxDaysAgo);
            }
        });
        return { datasets, maxDaysAgo };
    };

    // --- useEffect Hooks for Chart Management ---

    // Initialize charts on component mount
    React.useEffect(() => {
        mountedRef.current = true;
        console.log("Column2Content mounted"); // DEBUG

        // Initial render - only render Cardiac if it's expanded by default
        setTimeout(() => {
            if (mountedRef.current && expandedSection === "Cardiac") {
                console.log("Initial render of cardiac charts"); // DEBUG
                renderCardiacCharts();
            }
        }, 100);

        return () => {
            console.log("Column2Content unmounting, destroying all charts"); // DEBUG
            mountedRef.current = false;
            Object.values(chartInstances).forEach((chart: any) => {
                if (chart) chart.destroy();
            });
        };
    }, []);

    // Re-render charts when section expansion changes
    React.useEffect(() => {
        if (!mountedRef.current) return;
        console.log("expandedSections changed:", expandedSection); // DEBUG

        const sectionsConfig = [
            { id: 'Cardiac', renderFunc: renderCardiacCharts, charts: ['lvef-chart', 'rvsp-chart', 'hr-chart', 'bp-chart', 'qtc-chart'] },
            { id: 'Pulmonary', renderFunc: renderPulmonaryCharts, charts: ['rr-chart', 'spo2-chart', 'pco2-chart', 'hco3-chart'] },
            { id: 'Renal', renderFunc: renderRenalCharts, charts: ['egfr-chart'] },
            { id: 'Liver', renderFunc: renderLiverCharts, charts: ['inr-chart', 'albumin-chart', 'bilirubin-chart'] }
        ];

        sectionsConfig.forEach((section: any) => {
            if (expandedSection === section.id) {
                console.log(`Section ${section.id} is expanded - rendering charts`); // DEBUG

                // Use small timeout to give DOM time to update
                setTimeout(() => {
                    if (mountedRef.current && expandedSection === section.id) {
                        console.log(`Executing deferred render for ${section.id}`); // DEBUG
                        section.renderFunc();
                    }
                }, 50);
            } else {
                console.log(`Section ${section.id} is collapsed - cleaning up charts`); // DEBUG

                // Cleanup charts when section is collapsed
                section.charts.forEach((chartId: any) => {
                    if (chartInstances[chartId]) {
                        console.log(`Destroying chart: ${chartId}`); // DEBUG
                        chartInstances[chartId].destroy();
                        setChartInstances((prev: any) => {
                            const newState = { ...prev };
                            delete newState[chartId];
                            return newState;
                        });
                    }
                });
            }
        });
    }, [expandedSection]);

    // --- Chart Rendering Functions ---

    // Render Cardiac charts
    const renderCardiacCharts = () => {
        console.log("Attempting to render Cardiac charts..."); // DEBUG
        if (!mountedRef.current || expandedSection !== "Cardiac") {
            console.log("Skipping Cardiac render (not mounted or not expanded)"); // DEBUG
            return;
        }

        const { datasets: data, maxDaysAgo } = prepareCardiacData();
        console.log("Cardiac data prepared:", data); // DEBUG
        setCardiacData(data);

        const commonXAxisConfig = {
            min: 0,
            max: maxDaysAgo > 0 ? maxDaysAgo + Math.ceil(maxDaysAgo * 0.05) : 10,
        };

        // Create LVEF chart
        if (data.lvef.length > 0) {
            createChart('lvef-chart', 'Left Ventricular Ejection Fraction', data.lvef, {
                yAxisLabel: 'LVEF (%)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.studyDate}`,
                    `LVEF: ${item.y} ${item.unit}`,
                    `Measurement: ${item.measurement}`,
                    `Study: ${item.display_name}`
                ]
            });
        }

        // Create RVSP chart
        if (data.rvsp.length > 0) {
            createChart('rvsp-chart', 'Right Ventricular Systolic Pressure', data.rvsp, {
                yAxisLabel: 'RVSP (mmHg)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.studyDate}`,
                    `RVSP: ${item.y} ${item.unit}`,
                    `Measurement: ${item.measurement}`,
                    `Study: ${item.display_name}`
                ]
            });
        }

        // Create QTc chart
        if (data.qtc.length > 0) {
            createChart('qtc-chart', 'QTc Interval', data.qtc, {
                yAxisLabel: 'QTc (ms)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => {
                    // Determine risk category based on value
                    const value = item.y;
                    const gender = scanData?.demographics?.gender?.toLowerCase() || 'unknown';
                    let riskCategory = '';

                    if (gender.includes('female') || gender.includes('f')) {
                        if (value <= 450) riskCategory = 'Normal';
                        else if (value <= 470) riskCategory = 'Abnormal';
                        else riskCategory = value >= 500 ? 'Critical (high risk for torsades)' : 'Critical';
                    } else { // Male or unknown
                        if (value <= 430) riskCategory = 'Normal';
                        else if (value <= 470) riskCategory = 'Abnormal';
                        else riskCategory = value >= 500 ? 'Critical (high risk for torsades)' : 'Critical';
                    }

                    return [
                        `Date: ${item.studyDate}`,
                        `QTc: ${value} ms (${riskCategory})`,
                        `Rhythm: ${item.rhythm}`
                    ];
                }
            });
        }

        // Create Blood Pressure chart - Hide Legend
        if (data.bp.length > 0) {
            // Group BP data by measurement type
            createChart('bp-chart', 'Blood Pressure', data.bp, {
                yAxisLabel: 'Pressure (mmHg)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => {
                    return [
                        `Date: ${item.studyDate}`,
                        `${item.measurement}: ${item.y} mmHg`,
                        `Encounter: ${item.csn}`,
                        `Disposition: ${item.dispo}`,
                        `Measurements: ${item.count}`
                    ];
                }
            });
        }

        // Create HR chart
        if (data.hr.length > 0) {
            createChart('hr-chart', 'Heart Rate', data.hr, {
                yAxisLabel: 'HR (bpm)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => {
                    return [
                        `Date: ${item.startTime}`,
                        `HR: ${item.y} bpm`,
                        `Encounter: ${item.csn}`,
                        `Disposition: ${item.dispo}`,
                        `Measurements: ${item.count}`
                    ];
                }
            });
        }
    };

    // Render Pulmonary charts
    const renderPulmonaryCharts = () => {
        console.log("Attempting to render Pulmonary charts..."); // DEBUG
        if (!mountedRef.current || expandedSection !== "Pulmonary") {
            console.log("Skipping Pulmonary render (not mounted or not expanded)"); // DEBUG
            return;
        }

        const { datasets: data, maxDaysAgo } = preparePulmonaryData();
        console.log("Pulmonary data prepared:", data); // DEBUG
        setPulmonaryData(data);

        const commonXAxisConfig = {
            min: 0,
            max: maxDaysAgo > 0 ? maxDaysAgo + Math.ceil(maxDaysAgo * 0.05) : 10
        };

        if (data.rr.length > 0) {
            createChart('rr-chart', 'Resp Rate', data.rr, {
                yAxisLabel: 'RR (bpm)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.startTime}`,
                    `RR: ${item.y} bpm`,
                    `Encounter: ${item.csn}`,
                    `Disp: ${item.dispo}`,
                    `N: ${item.count}`
                ]
            });
        }

        if (data.spo2.length > 0) {
            createChart('spo2-chart', 'SpO2', data.spo2, {
                yAxisLabel: 'SpO2 (%)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.startTime}`,
                    `SpO2: ${item.y}%`,
                    `Encounter: ${item.csn}`,
                    `Disp: ${item.dispo}`,
                    `N: ${item.count}`
                ]
            });
        }

        if (data.pco2.length > 0) {
            createChart('pco2-chart', 'POC PCO2 (V)', data.pco2, {
                yAxisLabel: 'pCO2 (mmHg)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `pCO2: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }

        if (data.hco3.length > 0) {
            createChart('hco3-chart', 'POC HCO3 (V)', data.hco3, {
                yAxisLabel: 'HCO3 (mmol/L)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `HCO3: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }
    };

    // Render Renal charts
    const renderRenalCharts = () => {
        console.log("Attempting to render Renal charts..."); // DEBUG
        if (!mountedRef.current || expandedSection !== "Renal") {
            console.log("Skipping Renal render (not mounted or not expanded)"); // DEBUG
            return;
        }

        const { datasets: data, maxDaysAgo } = prepareRenalData();
        console.log("Renal data prepared:", data); // DEBUG
        setRenalData(data);

        const commonXAxisConfig = {
            min: 0,
            max: maxDaysAgo > 0 ? maxDaysAgo + Math.ceil(maxDaysAgo * 0.05) : 10
        };

        if (data.egfr.length > 0) {
            createChart('egfr-chart', 'eGFR', data.egfr, {
                yAxisLabel: 'eGFR (mL/min/1.73m2)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `eGFR: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }
    };

    // Render Liver charts
    const renderLiverCharts = () => {
        console.log("Attempting to render Liver charts..."); // DEBUG
        if (!mountedRef.current || expandedSection !== "Liver") {
            console.log("Skipping Liver render (not mounted or not expanded)"); // DEBUG
            return;
        }

        const { datasets: data, maxDaysAgo } = prepareLiverData();
        console.log("Liver data prepared:", data); // DEBUG
        setLiverData(data);

        const commonXAxisConfig = {
            min: 0,
            max: maxDaysAgo > 0 ? maxDaysAgo + Math.ceil(maxDaysAgo * 0.05) : 10
        };

        if (data.inr.length > 0) {
            createChart('inr-chart', 'INR', data.inr, {
                yAxisLabel: 'INR',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `INR: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }

        if (data.albumin.length > 0) {
            createChart('albumin-chart', 'Albumin', data.albumin, {
                yAxisLabel: 'Albumin (g/dL)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `Albumin: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }

        if (data.bilirubin.length > 0) {
            createChart('bilirubin-chart', 'Total Bilirubin', data.bilirubin, {
                yAxisLabel: 'Bilirubin (mg/dL)',
                xAxisConfig: commonXAxisConfig,
                showLegend: false,
                tooltipCallback: (item) => [
                    `Date: ${item.resultTime}`,
                    `Bilirubin: ${item.y} ${item.unit || ''}`,
                    `Flag: ${item.abnormalFlag || 'N/A'}`
                ]
            });
        }
    };

    const noDataIndicator = (
        <Row>
            <Col className="text-muted text-center">
                <small>No Data Available</small>
            </Col>
        </Row>
    );

    return (
        <>
            {/* Vital Signs Charts */}
            {expandedSection === "Cardiac" && (
                <Row className="mt-4 mb-4">
                    <Col md={12}>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>LVEF</h6>
                                <div className="chart-container">
                                    <canvas id='lvef-chart' className={cardiacData?.lvef?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        cardiacData?.lvef?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>RVSP</h6>
                                <div className="chart-container">
                                    <canvas id='rvsp-chart' className={cardiacData?.rvsp?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        cardiacData?.rvsp?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>Heart Rate</h6>
                                <div className="chart-container">
                                    <canvas id='hr-chart' className={cardiacData?.hr?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        cardiacData?.hr?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>Blood Pressure</h6>
                                <div className="chart-container">
                                    <canvas id='bp-chart' className={cardiacData?.bp?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        cardiacData?.bp?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>QTc Interval</h6>
                                <div>
                                    <small>
                                        <p className="text-muted">
                                            Normal: Males ≤ 430ms, Females ≤ 450ms<br />
                                            Abnormal: Males 431-470ms, Females 451-470ms<br />
                                            Critical: &gt; 470ms (increased arrhythmia risk, ≥ 500ms: risk for torsades de pointes)
                                        </p>
                                    </small>
                                </div>
                                <div className="chart-container">
                                    <canvas id='qtc-chart' className={cardiacData?.qtc?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        cardiacData?.qtc?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {expandedSection === "Pulmonary" && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>Respiratory Rate</h6>
                                <div className="chart-container">
                                    <canvas id='rr-chart' className={pulmonaryData?.rr?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        pulmonaryData?.rr?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>SpO2</h6>
                                <div className="chart-container">
                                    <canvas id='spo2-chart' className={pulmonaryData?.spo2?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        pulmonaryData?.spo2?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>PCO2 (V)</h6>
                                <div className="chart-container">
                                    <canvas id='pco2-chart' className={pulmonaryData?.pco2?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        pulmonaryData?.pco2?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>HCO3 (V)</h6>
                                <div className="chart-container">
                                    <canvas id='hco3-chart' className={pulmonaryData?.hco3?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        pulmonaryData?.hco3?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {expandedSection === "Renal" && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>eGFR</h6>
                                <div className="chart-container">
                                    <canvas id='egfr-chart' className={renalData?.egfr?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        renalData?.egfr?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}

            {expandedSection === "Liver" && (
                <Row className="mb-4">
                    <Col md={12}>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>INR</h6>
                                <div className="chart-container">
                                    <canvas id='inr-chart' className={liverData?.inr?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        liverData?.inr?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>Albumin</h6>
                                <div className="chart-container">
                                    <canvas id='albumin-chart' className={liverData?.albumin?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        liverData?.albumin?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                        <Card className="mb-2">
                            <Card.Body>
                                <h6>Total Bilirubin</h6>
                                <div className="chart-container">
                                    <canvas id='bilirubin-chart' className={liverData?.bilirubin?.length == 0 ? "physiology-canvas-hidden" : ""} />
                                    {
                                        liverData?.bilirubin?.length == 0 && (
                                            noDataIndicator
                                        )
                                    }
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </>
    );
};
